import { useEffect, useMemo, useState } from 'react';
import { FileText, Loader2, Search, X } from 'lucide-react';
import {
  elementKindLabel,
  filterActiveContentElements,
  type ContentElementRow,
} from '../../lib/contentElements';

export type BlockInsertPosition = 'top' | 'bottom';

const BLANK_SELECTION = '__blank__';

export type AddNewsletterContentResult =
  | { position: BlockInsertPosition; type: 'blank' }
  | { position: BlockInsertPosition; type: 'element'; element: ContentElementRow };

interface AddNewsletterContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  elements: ContentElementRow[];
  loading: boolean;
  onConfirm: (result: AddNewsletterContentResult) => void;
}

export default function AddNewsletterContentModal({
  isOpen,
  onClose,
  elements,
  loading,
  onConfirm,
}: AddNewsletterContentModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [insertPosition, setInsertPosition] = useState<BlockInsertPosition>('bottom');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setInsertPosition('bottom');
    setSelectedId(null);
  }, [isOpen]);

  const activeElements = useMemo(() => filterActiveContentElements(elements), [elements]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return activeElements;
    return activeElements.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.slug.toLowerCase().includes(q) ||
        e.kind.toLowerCase().includes(q),
    );
  }, [activeElements, searchQuery]);

  const selectedElement =
    selectedId && selectedId !== BLANK_SELECTION
      ? activeElements.find((e) => e.id === selectedId) ?? null
      : null;

  const canConfirm = selectedId === BLANK_SELECTION || selectedElement != null;

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (selectedId === BLANK_SELECTION) {
      onConfirm({ position: insertPosition, type: 'blank' });
      onClose();
      return;
    }
    if (selectedElement) {
      onConfirm({ position: insertPosition, type: 'element', element: selectedElement });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-stretch sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
      <button type="button" className="absolute inset-0 bg-black/50" aria-label="Close" onClick={onClose} />
      <div className="relative z-[71] w-full max-w-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden flex flex-col max-h-[min(90vh,640px)] min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl">
        <div className="shrink-0 px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Add content</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Choose a blank block or copy HTML from the element library into this newsletter.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="shrink-0 px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Insert at</span>
          <div
            className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50"
            role="group"
            aria-label="Insert position"
          >
            {(['top', 'bottom'] as const).map((pos) => (
              <button
                key={pos}
                type="button"
                onClick={() => setInsertPosition(pos)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  insertPosition === pos
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {pos === 'top' ? 'Top' : 'Bottom'}
              </button>
            ))}
          </div>
        </div>

        <div className="shrink-0 p-4 border-b border-gray-100 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search elements…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            <li>
              <button
                type="button"
                onClick={() => setSelectedId(BLANK_SELECTION)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-900/40 ${
                  selectedId === BLANK_SELECTION ? 'bg-emerald-50/60 dark:bg-emerald-900/20' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <FileText className="w-4 h-4 mt-0.5 text-gray-500 dark:text-gray-400 shrink-0" />
                  <div>
                    <div className="font-medium text-sm text-gray-900 dark:text-white">Blank block</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Empty markdown block — write or paste your own content
                    </div>
                  </div>
                </div>
              </button>
            </li>

            {loading ? (
              <li className="flex justify-center py-10">
                <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
              </li>
            ) : filtered.length === 0 ? (
              <li className="text-sm text-gray-500 dark:text-gray-400 text-center py-10 px-4">
                {searchQuery.trim() ? (
                  'No elements match your search.'
                ) : (
                  <>
                    No active elements. Add some under{' '}
                    <a href="/elements" className="text-emerald-600 dark:text-emerald-400 hover:underline">
                      Elements
                    </a>
                    .
                  </>
                )}
              </li>
            ) : (
              filtered.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(e.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-900/40 ${
                      selectedId === e.id ? 'bg-emerald-50/60 dark:bg-emerald-900/20' : ''
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900 dark:text-white">{e.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {elementKindLabel(e.kind)} · <span className="font-mono">{e.slug}</span>
                    </div>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>

        <div className="shrink-0 px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={handleConfirm}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
          >
            Insert at {insertPosition}
          </button>
        </div>
      </div>
    </div>
  );
}
